/**
 * Chat constants — shared across all chat-related code.
 *
 * Centralizes magic numbers, breakpoints, rate limits, and defaults
 * that were previously hardcoded across multiple files.
 */

import { activeEnv, getEnvOverride, hostForRole, type EnvChoice } from '../config/envSwitch';

// ── Layout ───────────────────────────────────────────────────
// iPad 11" landscape is ~1194 CSS px in Safari/Chrome. Treat it as compact
// chat layout: the desktop three-column Stream UI pushes the composer below
// the visible viewport, especially when the browser keyboard opens.
export const MOBILE_BREAKPOINT = 1280;
export const SIDEBAR_WIDTH = 400;
export const CATEGORIES_MENU_WIDTH = 84;

// ── API ──────────────────────────────────────────────────────
export const API_BASE_URL = process.env.EXPO_PUBLIC_API_URL || 'https://api.agdev.tmless.org';
// awesome-chat-api (the AI/RAG service — onboarding, voice moderation, attachment
// limits). Despite the "chat-api" host, this is NOT the self-hosted Agape.ChatApi.
// Per-env via EXPO_PUBLIC_CHAT_API_URL — dev (Preview) must set the Vercel var to
// chat-api.agdev.tmless.org, else the build falls through to .env.production and
// leaks the prod host into the dev bundle (CORS 403). CORE-371.
export const CHAT_API_BASE_URL = hostForRole(
  'chatApi',
  process.env.EXPO_PUBLIC_CHAT_API_URL || 'https://chat-api.agdev.tmless.org',
);
// CORE-371 (P7.3) — Agape.ChatApi, our self-hosted chat service that exposes the
// SignalR hub at /hubs/chat. DISTINCT host from CHAT_API_BASE_URL above (that one
// is awesome-chat-api). Needs its own public vhost (provisioned during the P7
// cutover); override per env with EXPO_PUBLIC_CHAT_HUB_URL.
// Same Preview gotcha as CHAT_API_BASE_URL above: the dev (Preview) Vercel build
// MUST set EXPO_PUBLIC_CHAT_HUB_URL=agape-chat.agdev.tmless.org, else it falls
// through to .env.production's prod hub (agape-chat.agapella.io) whose CORS
// rejects the rn-agape.vercel.app origin → AI/Wisdom/DM chat fails to connect
// ("Не удалось открыть чат"). The dev hub CORS-allows the dev origin. CORE-407.
// The build-time value is no longer the last word on web: `hostForRole` checks
// the actual address bar, so a bundle that baked the prod hub still connects to
// the dev hub when it is served from rn-agape.vercel.app or a preview URL. That
// removes the dependency on a Vercel env var which has now been lost twice and
// killed chat on the dev stand both times. Native keeps the baked value — there
// is no address bar there, and each EAS profile bakes its own env correctly.
export const CHAT_HUB_BASE_URL = hostForRole(
  'chatHub',
  process.env.EXPO_PUBLIC_CHAT_HUB_URL || 'https://agape-chat.agdev.tmless.org',
);
// CORE-407 — RU fallback for the ChatApi hub. Some RU ISPs hang the direct
// agape-chat socket (like getstream, CORE-119), leaving chat stuck on "Opening
// conversation…". ChatApiChannel connects to CHAT_HUB_BASE_URL with a timeout
// and, on failure, retries via this RU proxy. Non-RU users complete the direct
// attempt and never touch it.
//
// Both envs have a relay, and they differ by PATH as well as host, so neither a
// single baked variable nor `applyEnvHost` (which only swaps `://host`) can
// serve both. Resolve per active env instead:
//   dev  — api.rudev.tmless.org/agape-chat/ → agape-chat.agdev.tmless.org, added
//          by docs/infra/rudev-edge-add-chat.sh and rerouted through the DE relay
//          in CORE-724.
//   prod — chat-ru.agapella.io, an nginx vhost on the Moscow edge.
// Until now only .env.production carried the variable, so every dev / preview /
// e2e build had NO fallback at all (`canFallback === false`): an RU tester whose
// ISP hung the socket got a dead thread after one 10 s timeout.
const CHAT_HUB_RU_PROXY_BY_ENV: Record<EnvChoice, string> = {
  dev: 'https://api.rudev.tmless.org/agape-chat',
  prod: 'https://chat-ru.agapella.io',
  // CORE-1507 — the stand's hub on the Moscow edge. Read only under an override
  // (a native build switched to the stand); the stand's own web bundle bakes the
  // variable empty and keeps no relay (CORE-1468).
  staging: 'https://api.ru.ag1.tmless.org/agape-chat',
};

/**
 * RU relay base for the ChatApi hub, for the env that is active right now.
 *
 * Called at connect time rather than read as a module constant: the CORE-413 env
 * override is loaded asynchronously at startup, and a build flipped to the other
 * env must fall back to THAT env's relay — routing a dev-switched build through
 * the prod relay lands its dev token on the prod hub, which rejects it.
 * `EXPO_PUBLIC_CHAT_HUB_RU_PROXY` still wins when no override is in play, so a
 * one-off relay can be baked per build.
 *
 * CORE-1468 — and an EMPTY value wins too: it means «this build has no relay».
 * The stand (.env.staging) bakes it empty, but `||` read that as unset and fell
 * through to the dev relay — the stand's own `activeEnv()` is 'dev' — so a
 * stand token went to the dev hub and came back 401.
 */
export function chatHubRuProxy(): string {
  if (getEnvOverride()) return CHAT_HUB_RU_PROXY_BY_ENV[activeEnv()] ?? '';
  return ownEnvRelay(process.env.EXPO_PUBLIC_CHAT_HUB_RU_PROXY, CHAT_HUB_RU_PROXY_BY_ENV);
}

/**
 * CORE-1194 — релей, вкомпилированный для ДРУГОГО окружения, своим не считается.
 *
 * Дев-стенд собирается в prod-режиме expo и читает `.env.production`; джоба
 * `dev` подменяет хосты чата, а релей только вычёркивает из своего файла — и в
 * бандл попадал продовый `chat-ru.agapella.io`. Его CORS не знает
 * `rn-agape.vercel.app`, поэтому запасной путь чат-хаба на деве не работал
 * вовсе: после обрыва сети в консоли стоял отказ CORS на
 * `chat-ru.agapella.io/hubs/chat/negotiate` (QA 03.10.2026). Свой релей у дева
 * есть и отвечает этому origin.
 *
 * Пустое значение по-прежнему значит «релея нет» (CORE-1468), а адрес не из
 * таблицы — разовый релей сборки — по-прежнему выигрывает.
 */
function ownEnvRelay(baked: string | undefined, byEnv: Record<EnvChoice, string>): string {
  const own = byEnv[activeEnv()] || '';
  if (baked === undefined) return own;
  const foreign = baked !== '' && baked !== own && Object.values(byEnv).includes(baked);
  return foreign ? own : baked;
}

/**
 * Запасной РФ-путь к ИИ-сервису (`awesome-chat-api`).
 *
 * У хаба такой путь есть с CORE-407, у ИИ-сервиса не было — при том что сам
 * релей на московском эдже давно поднят и отдаёт те же ручки. Проверено
 * 29.09.2026: `chat-ru.agapella.io/api/v2/attachment-limits` и
 * `chat-api.agapella.io/api/v2/attachment-limits` возвращают байт в байт одно
 * и то же; на деве ту же роль играет `api.rudev.tmless.org/chat-api`.
 *
 * То есть дорога была построена, а клиент о ней не знал: каждый прямой вызов
 * ИИ-сервиса из браузера шёл на формируемый хост без всякого запасного пути.
 *
 * Пусто — «у этой сборки релея нет». Читается так же, как у хаба: явное пустое
 * значение выигрывает у умолчания (CORE-1468).
 */
const CHAT_API_RU_PROXY_BY_ENV: Record<EnvChoice, string> = {
  dev: 'https://api.rudev.tmless.org/chat-api',
  prod: 'https://chat-ru.agapella.io',
  staging: 'https://api.ru.ag1.tmless.org/chat-api',
};

/** Запасной РФ-путь к ИИ-сервису. Резолвится на вызов — см. `chatHubRuProxy`. */
export function chatApiRuProxy(): string {
  if (getEnvOverride()) return CHAT_API_RU_PROXY_BY_ENV[activeEnv()] ?? '';
  return ownEnvRelay(process.env.EXPO_PUBLIC_CHAT_API_RU_PROXY, CHAT_API_RU_PROXY_BY_ENV);
}

// ── AI Companions ────────────────────────────────────────────
export const DEFAULT_ASSISTANT_ID = process.env.EXPO_PUBLIC_DEFAULT_ASSISTANT_ID || 'sophia';
export const DEFAULT_ASSISTANT_STREAM_ID = process.env.EXPO_PUBLIC_DEFAULT_ASSISTANT_STREAM_ID || 'ai_sophia';

// ── Live Chat (SignalR) ──────────────────────────────────────
export const LIVE_CHAT_CONFIG = {
  // CORE-1612 — не больше `sendBurst` сообщений за `sendWindowMs`, как на сервере.
  sendBurst: 5,
  sendWindowMs: 10_000,
  maxMessageLength: 500,
  maxMessages: 200,
  // CORE-1253 — веб читает историю эфира с начала (REST), а не последние 50
  // из хаба; столько он и держит на экране, чтобы начало не отрезалось.
  historyMessages: 1000,
  // CORE-1820 — столько REST отдаёт за раз; что старше, листается `beforeId`.
  historyPageSize: 200,
} as const;

// ── DM Chat (SignalR) ────────────────────────────────────────
export const DM_CHAT_CONFIG = {
  rateLimitMs: 2000,
  maxMessageLength: 2000,
} as const;

// ── Stream Chat queries ──────────────────────────────────────
export const CHANNEL_QUERY_LIMIT = 100;
export const UNREAD_REFRESH_DEBOUNCE = 800;

// ── Donation amounts ─────────────────────────────────────────
export const DONATION_AMOUNTS = [1.99, 4.99, 9.99, 19.99];

// ── Stream Chat reaction types ───────────────────────────────
export const REACTION_STAR = 'star';

// ── Channel ID prefixes ──────────────────────────────────────
// Two distinct channel families — keep them separate.
//   ``circle-{guid}``               — generic master-subscription / private-circle
//                                     channels (legacy, see ChatsScreen).
//   ``master-circle-{masterProfileId}`` — per-master event-thread channel that
//                                     the backend's EventThreadService posts
//                                     ``live_event`` parent messages into. The
//                                     "Начать обсуждение" side panel binds
//                                     here so the discussion under a Live or
//                                     Practice resolves to a real Stream
//                                     Chat thread the master also sees.
export const CIRCLE_CHANNEL_PREFIX = 'circle-';
export const EVENT_THREAD_CHANNEL_PREFIX = 'master-circle-';
export const WISDOM_COUNCIL_PREFIX = 'wisdom-council';
